// Left navigation
import { useState } from '../core/react.js';
import { T } from '../core/theme.js';
import { SyncDot } from '../ui/widgets.js';
import { Icon } from '../ui/icons.js';
import { useInstallPrompt } from '../core/util.js';
import { VIEW_NAMES } from './topbar.js';
import { attention } from '../core/deadlines.js';

export function Sidebar({
  view,
  setView,
  project,
  projects,
  setProject,
  synced,
  lastFetch,
  user,
  onAddProject,
  onSignOut,
  height = '100vh'   // phones pass the visible height (iOS counts its toolbar in 100vh)
}) {
  const install = useInstallPrompt();
  const [open, setOpen] = useState(false);
  const navItems = [{
    heading: 'Budgets'
  }, {
    id: 'dashboard', icon: Icon.grid, label: VIEW_NAMES.dashboard
  }, {
    id: 'project', icon: Icon.plug, label: VIEW_NAMES.project
  }, {
    id: 'budget', icon: Icon.table, label: VIEW_NAMES.budget
  }, {
    id: 'subbudgets', icon: Icon.edit, label: VIEW_NAMES.subbudgets
  }, {
    id: 'invoices', icon: Icon.receipt, label: VIEW_NAMES.invoices
  }, {
    id: 'pdf', icon: Icon.doc, label: VIEW_NAMES.pdf
  }, {
    id: 'approvals', icon: Icon.key, label: VIEW_NAMES.approvals
  }, {
    heading: 'Production'
  }, {
    id: 'schedule', icon: Icon.calendar, label: VIEW_NAMES.schedule
  }, {
    id: 'crew', icon: Icon.users, label: VIEW_NAMES.crew
  }, {
    id: 'deliveries', icon: Icon.refresh, label: VIEW_NAMES.deliveries
  }, {
    // the number: deadlines overdue or due within a week
    id: 'deadlines', icon: Icon.check, label: VIEW_NAMES.deadlines, badge: attention(project?.deadlines)
  }, {
    id: 'files', icon: Icon.link, label: VIEW_NAMES.files
  }, {
    heading: 'Suppliers'
  }, {
    id: 'suppliers', icon: Icon.users, label: VIEW_NAMES.suppliers
  }, {
    id: 'tenders', icon: Icon.prism, label: VIEW_NAMES.tenders
  }, {
    id: 'quotes', icon: Icon.quote, label: VIEW_NAMES.quotes
  }, {
    heading: 'Admin'
  }, {
    id: 'activity', icon: Icon.log, label: VIEW_NAMES.activity
  }, {
    id: 'team', icon: Icon.users, label: VIEW_NAMES.team
  }, {
    id: 'settings', icon: Icon.cog, label: VIEW_NAMES.settings
  }, ...(user?.role === 'Admin' ? [{
    id: 'system', icon: Icon.shield, label: VIEW_NAMES.system
  }] : [])];
  const FREELANCER_VIEWS = ['schedule', 'crew', 'deliveries', 'files', 'team'];
  const navShown = (user?.role === 'Freelancer' ? navItems.filter(n => n.heading || FREELANCER_VIEWS.includes(n.id)) : navItems)
    .filter((n, i, arr) => !n.heading || (arr[i + 1] && !arr[i + 1].heading));
  return React.createElement("div", {
    style: {
      width: 226,
      background: T.navy,
      display: 'flex',
      flexDirection: 'column',
      height,
      flexShrink: 0,
      position: 'relative',
      overflow: 'hidden'
    }
  }, React.createElement("div", {
    style: {
      position: 'absolute',
      inset: 0,
      opacity: .025,
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      backgroundSize: '160px',
      pointerEvents: 'none'
    }
  }), React.createElement("div", {
    style: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 250,
      background: 'radial-gradient(ellipse at 10% 100%, rgba(128,130,133,.28) 0%, transparent 65%)',
      pointerEvents: 'none'
    }
  }), React.createElement("div", {
    style: {
      padding: '20px 20px 16px',
      borderBottom: '1px solid rgba(255,255,255,.07)',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 7
    }
  }, React.createElement("img", {
    src: "./img/sidebar-wordmark.png",
    alt: "glassbox",
    style: {
      height: 24,
      width: 'auto',
      display: 'block'
    }
  }), React.createElement("div", {
    style: {
      fontSize: 9.5,
      color: 'rgba(255,255,255,.6)',
      letterSpacing: '0.34em',
      fontWeight: 600
    }
  }, "PRISM")), React.createElement("div", {
    style: {
      padding: '12px 20px 0',
      position: 'relative',
      zIndex: 20
    }
  }, React.createElement("div", {
    style: {
      fontSize: 8,
      color: 'rgba(255,255,255,.3)',
      letterSpacing: '0.15em',
      marginBottom: 5
    }
  }, "ACTIVE PROJECT"), React.createElement("button", {
    onClick: () => setOpen(!open),
    style: {
      border: 'none',
      padding: '8px 10px',
      width: '100%',
      textAlign: 'left',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      background: 'rgba(255,255,255,.06)',
      borderRadius: 2,
      cursor: 'pointer'
    }
  }, React.createElement("div", {
    style: {
      fontSize: 11,
      color: T.white,
      fontWeight: 500,
      lineHeight: 1.4,
      maxWidth: 162
    }
  }, project?.name), React.createElement("span", {
    style: {
      color: 'rgba(255,255,255,.4)',
      marginTop: 1,
      flexShrink: 0
    }
  }, React.createElement(Icon.chev, {
    s: 11,
    c: "rgba(255,255,255,.4)",
    d: open ? 'up' : 'down'
  }))), open && React.createElement("div", {
    style: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: '100%',
      zIndex: 50,
      background: '#4A4B50',
      boxShadow: '0 12px 32px rgba(0,0,0,.4)',
      borderTop: '1px solid rgba(255,255,255,.1)'
    }
  }, projects.map(p => React.createElement("button", {
    key: p.id,
    type: 'button',
    onClick: () => {
      setProject(p);
      setOpen(false);
      setView('budget');
    },
    style: {
      display: 'block',
      width: '100%',
      textAlign: 'left',
      padding: '11px 20px',
      cursor: 'pointer',
      borderBottom: '1px solid rgba(255,255,255,.06)',
      transition: 'background .15s'
    },
    onMouseEnter: e => e.currentTarget.style.background = 'rgba(255,255,255,.07)',
    onMouseLeave: e => e.currentTarget.style.background = 'transparent'
  }, React.createElement("div", {
    style: {
      fontSize: 11,
      color: T.white,
      fontWeight: 500
    }
  }, p.name), React.createElement("div", {
    style: {
      fontSize: 10,
      color: 'rgba(255,255,255,.35)',
      marginTop: 2
    }
  }, p.client, " \xB7 ", p.date))))), React.createElement("div", {
    style: {
      padding: '6px 12px 10px',
      position: 'relative',
      zIndex: 10
    }
  }, !['Read-only', 'Freelancer'].includes(user?.role) && React.createElement("button", {
    onClick: onAddProject,
    style: {
      width: '100%',
      padding: '7px 10px',
      background: 'rgba(255,255,255,.07)',
      border: '1px dashed rgba(255,255,255,.15)',
      color: 'rgba(255,255,255,.5)',
      fontSize: 11,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      transition: 'all .15s'
    },
    onMouseEnter: e => {
      e.currentTarget.style.background = 'rgba(255,255,255,.12)';
      e.currentTarget.style.color = 'rgba(255,255,255,.8)';
    },
    onMouseLeave: e => {
      e.currentTarget.style.background = 'rgba(255,255,255,.07)';
      e.currentTarget.style.color = 'rgba(255,255,255,.5)';
    }
  }, React.createElement(Icon.plus, {
    s: 11,
    c: "currentColor"
  }), " New Project"), !['Read-only', 'Freelancer'].includes(user?.role) && projects.length > 0 && React.createElement("button", {
    onClick: () => setView('copy'),
    'aria-current': view === 'copy' ? 'page' : undefined,
    style: {
      width: '100%',
      marginTop: 4,
      padding: '4px 10px',
      color: view === 'copy' ? T.white : 'rgba(255,255,255,.45)',
      fontSize: 10.5,
      textDecoration: 'underline',
      textUnderlineOffset: 2
    }
  }, "or copy a past event")), React.createElement("nav", {
    style: {
      flex: 1,
      paddingTop: 4,
      position: 'relative',
      overflowY: 'auto'
    }
  }, navShown.map(({
    id,
    icon: Ic,
    label,
    heading,
    badge
  }) => {
    if (heading) return React.createElement("div", {
      key: 'h-' + heading,
      style: {
        padding: '14px 20px 4px',
        fontSize: 10.5,
        color: 'rgba(255,255,255,.6)',
        fontWeight: 600
      }
    }, heading);
    const active = view === id;
    return React.createElement("button", {
      key: id,
      type: 'button',
      'aria-current': active ? 'page' : undefined,
      onClick: () => {
        setView(id);
      },
      style: {
        width: '100%',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '7px 20px',
        cursor: 'pointer',
        transition: 'background .15s',
        background: active ? 'rgba(255,255,255,.1)' : 'transparent',
        borderLeft: `2px solid ${active ? T.ivory : 'transparent'}`
      },
      onMouseEnter: e => {
        if (!active) e.currentTarget.style.background = 'rgba(255,255,255,.06)';
      },
      onMouseLeave: e => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }
    }, React.createElement("span", {
      style: {
        color: active ? T.white : 'rgba(255,255,255,.45)',
        display: 'flex',
        flexShrink: 0
      }
    }, React.createElement(Ic, {
      s: 15,
      c: active ? T.white : 'rgba(255,255,255,.45)'
    })), React.createElement("span", {
      style: {
        fontSize: 13,
        color: active ? T.white : 'rgba(255,255,255,.5)',
        fontWeight: active ? 500 : 400
      }
    }, label), badge > 0 && React.createElement("span", {
      'aria-label': `${badge} need attention`,
      style: {
        marginLeft: 'auto',
        minWidth: 18,
        padding: '1px 6px',
        borderRadius: 9,
        background: '#E0A100',
        color: '#1E1E1E',
        fontSize: 10.5,
        fontWeight: 700,
        textAlign: 'center'
      }
    }, badge));
  })), React.createElement("div", {
    style: {
      padding: '10px 20px',
      borderTop: '1px solid rgba(255,255,255,.07)',
      position: 'relative'
    }
  }, React.createElement(SyncDot, {
    synced: synced,
    lastFetch: lastFetch
  })), React.createElement("div", {
    style: {
      padding: '12px 20px 18px',
      position: 'relative'
    }
  }, React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 10
    }
  }, React.createElement("div", {
    style: {
      width: 30,
      height: 30,
      borderRadius: '50%',
      background: T.navyM,
      border: '1.5px solid rgba(255,255,255,.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 11,
      color: T.white,
      fontWeight: 700,
      flexShrink: 0
    }
  }, user?.init || '?'), React.createElement("div", {
    style: {
      flex: 1,
      minWidth: 0
    }
  }, React.createElement("div", {
    style: {
      fontSize: 12,
      color: T.white,
      fontWeight: 500
    }
  }, user?.name || '—'), React.createElement("div", {
    style: {
      fontSize: 10,
      color: 'rgba(255,255,255,.3)'
    }
  }, user?.role || '—')), React.createElement("button", {
    onClick: onSignOut,
    title: "Sign out",
    style: {
      fontSize: 10,
      color: 'rgba(255,255,255,.4)',
      letterSpacing: '0.06em'
    }
  }, "Sign out")), React.createElement("a", {
    href: '/privacy.html', target: '_blank', rel: 'noopener',
    style: { display: 'block', marginTop: 8, fontSize: 10, color: 'rgba(255,255,255,.35)', textDecoration: 'none' }
  }, "Privacy notice"), install && React.createElement("button", {
    onClick: install,
    style: {
      marginTop: 12,
      width: '100%',
      padding: '7px',
      border: '1px solid rgba(255,255,255,.18)',
      color: 'rgba(255,255,255,.75)',
      fontSize: 10.5,
      letterSpacing: '0.1em',
      fontWeight: 600
    }
  }, "INSTALL APP")));
}
